import { Bot, Clock, MessageSquare } from 'lucide-react'
import {
  DASHBOARD_PERIOD_LABELS,
  type DashboardPeriod,
  type DashboardStats,
  type MessageTrend,
} from '@/services/dashboardApi'
import type { QualityMetrics } from './dashboardMetrics'
import { dashboardCopy } from './dashboardCopy'
import { MessageTrendSparkline } from './MessageTrendSparkline'
import { StatCard } from './StatCard'

export interface KpiRowProps {
  stats: DashboardStats
  metrics: QualityMetrics
  period: DashboardPeriod
  trendPoints?: MessageTrend[]
  trendLoading?: boolean
}

function formatDelta(value: number | null | undefined, suffix = ''): string {
  if (value === null || value === undefined) return dashboardCopy.noPreviousPeriod
  if (value === 0) return dashboardCopy.sameAsPrevious

  const absolute = Math.abs(value)
  const formatted = Number.isInteger(absolute) ? absolute.toLocaleString() : absolute.toFixed(1)
  const sign = value > 0 ? '+' : '−'
  return dashboardCopy.previousPeriodDelta(`${sign}${formatted}${suffix}`)
}

/** Una sola fila de 4 KPIs: el detalle secundario no se repite en otra sección. */
export function KpiRow({
  stats,
  metrics,
  period,
  trendPoints,
  trendLoading,
}: KpiRowProps) {
  const { overview } = stats
  const { botRatio, humanRatio, avgMinutes } = metrics
  const unread = overview.conversationsUnread
  const periodLabel = DASHBOARD_PERIOD_LABELS[period].toLowerCase()
  const messages = overview.messagesInPeriod ?? overview.messagesToday
  const closedInPeriod = overview.conversationsClosedInPeriod ?? overview.conversationsClosed
  const trendDays = period === '30d' ? 30 : 7

  const responseValue = avgMinutes === null
    ? null
    : avgMinutes >= 60
      ? Number(overview.avgResponseTimeHours).toFixed(1)
      : avgMinutes === 0
        ? '< 1'
        : avgMinutes
  const responseUnit = avgMinutes === null ? undefined : avgMinutes >= 60 ? 'h' : 'm'

  return (
    <section aria-label="KPIs del período" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatCard
        icon={MessageSquare}
        iconClassName="text-amber-600"
        label={dashboardCopy.conversationsOpen}
        value={overview.conversationsOpen}
        detail={`${unread} sin leer`}
        formula="Conversaciones con estado abierto ahora mismo. No depende del período elegido."
        badge={
          unread > 0 ? (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-xs font-bold text-white">
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null
        }
      />

      <StatCard
        icon={MessageSquare}
        label={dashboardCopy.messages}
        value={messages === 0 ? null : messages.toLocaleString()}
        detail={`${overview.messagesIncoming.toLocaleString()} recibidos · ${overview.messagesOutgoing.toLocaleString()} enviados · ${closedInPeriod.toLocaleString()} cierres`}
        formula={`Entrantes + salientes de ${periodLabel}.`}
        delta={formatDelta(overview.deltas?.messages)}
        trend={(
          <MessageTrendSparkline
            points={trendPoints}
            days={trendDays}
            loading={trendLoading}
          />
        )}
      />

      <StatCard
        icon={Bot}
        iconClassName="text-indigo-600"
        label={dashboardCopy.attendedByAssistant}
        value={botRatio}
        unit="%"
        detail={
          botRatio === null
            ? dashboardCopy.withoutData
            : `${humanRatio}% lo atendió el equipo`
        }
        formula={`Conversaciones de ${periodLabel} que no pasaron al equipo ÷ total de conversaciones del período.`}
        delta={formatDelta(overview.deltas?.attendedByAssistant, ' pts')}
      />

      <StatCard
        icon={Clock}
        iconClassName="text-emerald-600"
        label={dashboardCopy.assistantResponseTime}
        value={responseValue}
        unit={responseUnit}
        detail="mediana"
        formula={`Mediana de horas entre el primer mensaje del cliente y la primera respuesta del asistente en ${periodLabel}.`}
        delta={formatDelta(overview.deltas?.responseTimeHours, ' h')}
      />
    </section>
  )
}
